import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Mesh,
  type Scene,
  ShaderMaterial,
  Vector3,
} from 'three';

const MAX = 28;
/** Jak długo (s) punkt smugi zostaje za ostrzem. */
const LIFE = 0.22;

interface Sample {
  base: Vector3;
  tip: Vector3;
  age: number;
}

/**
 * Smuga za bronią: wstęga rozpięta między podstawą a czubkiem ostrza w kolejnych klatkach.
 * Widać dokładny tor zamachu (a nie tylko płaski łuk na ziemi), jaśniejsza przy czubku, szybko gaśnie.
 */
export class WeaponTrail {
  private readonly samples: Sample[] = [];
  private readonly geometry = new BufferGeometry();
  private readonly positions = new Float32Array(MAX * 2 * 3);
  private readonly uvs = new Float32Array(MAX * 2 * 2);
  private readonly material: ShaderMaterial;
  private readonly mesh: Mesh;

  constructor(scene: Scene) {
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('uv', new BufferAttribute(this.uvs, 2));
    const index: number[] = [];
    for (let i = 0; i < MAX - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geometry.setIndex(index);
    this.material = new ShaderMaterial({
      uniforms: { color: { value: new Color('#ffffff') } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform vec3 color;
        varying vec2 vUv;
        void main() {
          // uv.x: 0 = najstarszy punkt, 1 = ostrze teraz; uv.y: 0 = podstawa, 1 = czubek.
          float age = clamp(vUv.x, 0.0, 1.0);
          float along = clamp(vUv.y, 0.0, 1.0);
          float a = age * age * smoothstep(0.0, 0.35, along) * (0.55 + 0.45 * along);
          vec3 c = mix(color, vec3(1.0), along * age * 0.6);
          gl_FragColor = vec4(c * 2.0, clamp(a, 0.0, 1.0) * 0.85);
        }`,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  setColor(color: string): void {
    this.material.uniforms.color!.value.set(color);
  }

  /** `active` = trwa zamach; nowe punkty dokładamy tylko wtedy, stare i tak gasną. */
  update(dt: number, active: boolean, base: Vector3, tip: Vector3): void {
    for (const s of this.samples) s.age += dt;
    while (this.samples.length && this.samples[0]!.age > LIFE) this.samples.shift();
    if (active) {
      const last = this.samples[this.samples.length - 1];
      // Nowy punkt tylko, gdy ostrze się ruszyło — w bezruchu smuga nie „puchnie”.
      if (!last || last.tip.distanceToSquared(tip) > 0.0004) {
        this.samples.push({ base: base.clone(), tip: tip.clone(), age: 0 });
        if (this.samples.length > MAX) this.samples.shift();
      }
    }
    const n = this.samples.length;
    this.mesh.visible = n >= 2;
    if (n < 2) return;
    for (let i = 0; i < MAX; i++) {
      const s = this.samples[Math.min(i, n - 1)]!;
      const u = i < n ? 1 - s.age / LIFE : 0;
      this.positions.set([s.base.x, s.base.y, s.base.z, s.tip.x, s.tip.y, s.tip.z], i * 6);
      this.uvs.set([u, 0, u, 1], i * 4);
    }
    this.geometry.attributes.position!.needsUpdate = true;
    this.geometry.attributes.uv!.needsUpdate = true;
    this.geometry.setDrawRange(0, (n - 1) * 6);
  }
}
